import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { ProjectWithMetadata, ProjectSelectorState } from '@/types/project-selector';
import type { Project } from '@/types';

const STORAGE_KEY_PREFIX = 'project-selector-state';
const MAX_RECENT_PROJECTS = 10;

function getStorageKey(companyId?: string): string {
  return companyId ? `${STORAGE_KEY_PREFIX}-${companyId}` : STORAGE_KEY_PREFIX;
}

// Base project type that both Project and ProjectWithMetadata share
type BaseProject = {
  id: string;
  code: string;
  name: string;
  clientId: string;
  clientName: string;
  billable: boolean;
  teamSize: number;
  isFavourite?: boolean;
};

/**
 * Hook to manage project selector state including favourites and recently used projects
 */
export function useProjectSelector(projects: Project[] | ProjectWithMetadata[], companyId?: string) {
  const storageKey = getStorageKey(companyId);
  const storageKeyRef = useRef(storageKey);
  const skipNextSaveRef = useRef(false);

  const [state, setState] = useState<ProjectSelectorState>(() => {
    // Load from localStorage on mount
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          favourites: parsed.favourites || [],
          recentlyUsed: parsed.recentlyUsed || [],
        };
      }
    } catch (error) {
      console.error('Failed to load project selector state:', error);
    }
    return { favourites: [], recentlyUsed: [] };
  });

  // Reload state from localStorage when the company (and therefore the key) changes
  useEffect(() => {
    const keyChanged = storageKeyRef.current !== storageKey;
    storageKeyRef.current = storageKey;
    if (!keyChanged) return;

    skipNextSaveRef.current = true;
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        setState({
          favourites: parsed.favourites || [],
          recentlyUsed: parsed.recentlyUsed || [],
        });
      } else {
        setState({ favourites: [], recentlyUsed: [] });
      }
    } catch (error) {
      console.error('Failed to load project selector state:', error);
      setState({ favourites: [], recentlyUsed: [] });
    }
  }, [storageKey]);

  // Persist state to localStorage whenever it changes
  useEffect(() => {
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }
    try {
      localStorage.setItem(storageKeyRef.current, JSON.stringify(state));
    } catch (error) {
      console.error('Failed to save project selector state:', error);
    }
  }, [state]);

  // Toggle favourite status
  const toggleFavourite = useCallback((projectId: string) => {
    setState((prev) => {
      const isFavourite = prev.favourites.includes(projectId);
      return {
        ...prev,
        favourites: isFavourite
          ? prev.favourites.filter((id) => id !== projectId)
          : [...prev.favourites, projectId],
      };
    });
  }, []);

  // Record project access
  const recordProjectAccess = useCallback((projectId: string) => {
    setState((prev) => {
      // Remove existing entry if present
      const filtered = prev.recentlyUsed.filter((item) => item.id !== projectId);
      
      // Add to front with current timestamp
      const updated = [
        { id: projectId, timestamp: Date.now() },
        ...filtered,
      ].slice(0, MAX_RECENT_PROJECTS);

      return {
        ...prev,
        recentlyUsed: updated,
      };
    });
  }, []);

  // Get enriched projects with favourite and recently used metadata
  const enrichedProjects = useMemo(() => {
    return projects.map((project) => ({
      ...project,
      isFavourite: state.favourites.includes(project.id),
      lastAccessedDate: state.recentlyUsed.find((item) => item.id === project.id)
        ? new Date(state.recentlyUsed.find((item) => item.id === project.id)!.timestamp)
        : undefined,
    }));
  }, [projects, state.favourites, state.recentlyUsed]);

  // Get favourite projects
  const favouriteProjects = useMemo(() => {
    return enrichedProjects
      .filter((p) => p.isFavourite)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [enrichedProjects]);

  // Get recently used projects (excluding favourites)
  const recentProjects = useMemo(() => {
    const recentIds = state.recentlyUsed.map((item) => item.id);
    return enrichedProjects
      .filter((p) => recentIds.includes(p.id) && !p.isFavourite)
      .sort((a, b) => {
        const aTime = a.lastAccessedDate?.getTime() || 0;
        const bTime = b.lastAccessedDate?.getTime() || 0;
        return bTime - aTime;
      })
      .slice(0, MAX_RECENT_PROJECTS);
  }, [enrichedProjects, state.recentlyUsed]);

  return {
    enrichedProjects,
    favouriteProjects,
    recentProjects,
    toggleFavourite,
    recordProjectAccess,
    isFavourite: (projectId: string) => state.favourites.includes(projectId),
  };
}
